import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, UserCheck, BookOpen, CalendarCheck,
  ClipboardList, DollarSign, Bell, Library, Bus, BarChart2,
  ChevronDown, ChevronRight, LogOut, X, BookMarked, UserPlus, Settings, FileText,
  Briefcase, MessageCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';

const navConfig = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', roles: ['admin','teacher','student','parent','father','mother','driver'] },
  {
    label: 'Enquiry', icon: UserPlus, roles: ['admin'],
    children: [
      { label: 'All Enquiries', path: '/admission/enquiries', roles: ['admin'] },
      { label: 'New Enquiry', path: '/admission/enquiry', roles: ['admin'] },
    ]
  },
  {
    label: 'Homework', icon: BookMarked, roles: ['admin','teacher','parent','father','mother','student'],
    children: [
      { label: 'Homework', path: '/homework', roles: ['admin','teacher','parent','father','mother','student'] },
      { label: 'Add Homework', path: '/homework/add', roles: ['admin','teacher'] },
      { label: 'Reports', path: '/homework/reports', roles: ['admin','teacher'] },
    ]
  },
  {
    label: 'Students', icon: Users, roles: ['admin','teacher'],
    children: [
      { label: 'All Students', path: '/students', roles: ['admin','teacher'] },
      { label: "Today's Birthdays", path: '/students/birthdays', roles: ['admin','teacher'] },
      { label: 'New Admission', path: '/students/admission', roles: ['admin'] },
      { label: 'Class Allocation', path: '/students/allocation', roles: ['admin'] },
      { label: 'Promotion', path: '/students/promotion', roles: ['admin'] },
      { label: 'Drop Out', path: '/students/dropout', roles: ['admin'] },
      { label: 'Certificates', path: '/students/certificates', roles: ['admin'] },
    ]
  },
  {
    label: 'Staff', icon: UserCheck, roles: ['admin'],
    children: [
      { label: 'All Staff', path: '/staff', roles: ['admin'] },
      { label: 'Register Staff', path: '/staff/register', roles: ['admin'] },
    ]
  },
  {
    label: 'Academic', icon: BookOpen, roles: ['admin','teacher'],
    children: [
      { label: 'Classes', path: '/academic/classes', roles: ['admin'] },
      { label: 'Subjects', path: '/academic/subjects', roles: ['admin'] },
      { label: 'Timetable', path: '/academic/timetable', roles: ['admin','teacher'] },
      { label: 'Lesson Plans', path: '/academic/lesson-plans', roles: ['admin','teacher'] },
      { label: 'Teacher Assignment', path: '/settings/teacher-mapping', roles: ['admin'] },
    ]
  },
  {
    label: 'Attendance', icon: CalendarCheck, roles: ['admin','teacher','parent','father','mother','student'],
    children: [
      { label: 'Student Attendance', path: '/attendance/students', roles: ['admin','teacher','parent','father','mother','student'] },
      { label: 'Staff Attendance', path: '/attendance/staff', roles: ['admin'] },
      { label: 'Reports', path: '/attendance/reports', roles: ['admin','teacher'] },
    ]
  },
  {
    label: 'Examinations', icon: ClipboardList, roles: ['admin','teacher','student','parent','father','mother'],
    children: [
      { label: 'Exam Schedule', path: '/exams/schedule', roles: ['admin','teacher','student','parent','father','mother'] },
      { label: 'Marks Entry', path: '/exams/marks', roles: ['admin','teacher'] },
      { label: 'Results', path: '/exams/results', roles: ['admin','teacher','student','parent','father','mother'] },
      { label: 'Hall Ticket', path: '/exams/hall-ticket', roles: ['admin'] },
    ]
  },
  {
    label: 'Fees', icon: DollarSign, roles: ['admin','parent','father','mother','student'],
    children: [
      { label: 'Fee Structure',       path: '/fees/structure',   roles: ['admin'] },
      { label: 'Collect Fee',         path: '/fees/collection',  roles: ['admin'] },
      { label: 'Receipts',            path: '/fees/receipts',    roles: ['admin','parent','father','mother','student'] },
      { label: 'Due Tracking',        path: '/fees/dues',        roles: ['admin'] },
      { label: 'Collection Report',   path: '/fees/report',      roles: ['admin'] },
    ]
  },
  {
    label: 'Communication', icon: Bell, roles: ['admin','teacher','parent','father','mother','student'],
    children: [
      { label: 'Announcements',     path: '/communication/announcements', roles: ['admin','teacher','parent','father','mother','student'],
        roleLabels: { parent: 'Alerts & Notifications', father: 'Alerts & Notifications', mother: 'Alerts & Notifications', teacher: 'Alerts & Notifications', student: 'Alerts & Notifications' } },
      { label: 'WhatsApp Messages', path: '/communication/whatsapp',      roles: ['admin'] },
    ]
  },
  {
    label: 'Library', icon: Library, roles: ['admin','teacher','student'],
    children: [
      { label: 'Book Catalog', path: '/library/catalog', roles: ['admin','teacher','student'] },
      { label: 'Issue / Return', path: '/library/issue-return', roles: ['admin'] },
    ]
  },
  {
    label: 'Transport', icon: Bus, roles: ['admin','parent','father','mother','student','driver'],
    children: [
      { label: 'Live Tracking',  path: '/transport/live',          roles: ['admin'] },
      { label: 'Routes',         path: '/transport/routes',         roles: ['admin','driver'] },
      { label: 'Vehicles',       path: '/transport/vehicles',       roles: ['admin','driver'] },
      { label: 'Pickup Points',  path: '/transport/pickup-points',  roles: ['admin','driver'] },
    ]
  },
  {
    label: 'Question Papers', icon: FileText, roles: ['admin'],
    children: [
      { label: 'All Papers', path: '/question-papers', roles: ['admin'] },
      { label: 'FA Schedule Config', path: '/question-papers/fa-schedule', roles: ['admin'] },
    ]
  },
  {
    label: 'HR & Payroll', icon: Briefcase, roles: ['admin'],
    children: [
      { label: 'Teacher Attendance', path: '/hr/attendance',        roles: ['admin'] },
      { label: 'Leave Requests',     path: '/hr/leave',             roles: ['admin'] },
      { label: 'Salary Structures',  path: '/hr/salary-structures', roles: ['admin'] },
      { label: 'Salary Slips',       path: '/hr/salary-slips',      roles: ['admin'] },
    ]
  },
  {
    label: 'My Profile', icon: Briefcase, roles: ['teacher'],
    children: [
      { label: 'My Attendance',  path: '/hr/attendance',  roles: ['teacher'] },
      { label: 'My Pay Slips',   path: '/hr/salary-slips', roles: ['teacher'] },
      { label: 'My Salary',      path: '/hr/salary-structures', roles: ['teacher'] },
    ]
  },
  {
    label: 'WhatsApp Hub', icon: MessageCircle, roles: ['admin'],
    children: [
      { label: 'Inbox',          path: '/whatsapp/inbox',         roles: ['admin'] },
      { label: 'Conversations',  path: '/whatsapp/conversations',  roles: ['admin'] },
      { label: 'Configuration',  path: '/whatsapp/config',         roles: ['admin'] },
    ]
  },
  {
    label: 'Settings', icon: Settings, roles: ['admin'],
    children: [
      { label: 'Academic & Policy',  path: '/settings/academic',           roles: ['admin'] },
      { label: 'User Management',    path: '/settings/users',              roles: ['admin'] },
      { label: 'Import Students',    path: '/settings/student-import',     roles: ['admin'] },
    ]
  },
  {
    label: 'Reports', icon: BarChart2, roles: ['admin'],
    children: [
      { label: 'Overview', path: '/reports', roles: ['admin'] },
      { label: 'Attendance Summary', path: '/reports/attendance', roles: ['admin'] },
      { label: 'Financial Report', path: '/reports/financial', roles: ['admin'] },
    ]
  },
];

function NavItem({ item, role }) {
  const [open, setOpen] = useState(false);
  if (!item.roles.includes(role)) return null;
  if (item.children) {
    const visibleChildren = item.children.filter(c => c.roles.includes(role));
    if (visibleChildren.length === 0) return null;
    return (
      <div>
        <button onClick={() => setOpen(o => !o)}
          className="w-full flex items-center gap-2 px-2 py-1 rounded-lg text-sm hover:bg-gray-100 transition-colors" style={{ color: '#000' }}>
          <item.icon size={16} className="flex-shrink-0" />
          <span className={`flex-1 text-left ${open ? 'font-semibold' : 'font-normal'}`}>{item.label}</span>
          {open ? <ChevronDown size={13} className="text-gray-500" /> : <ChevronRight size={13} className="text-gray-500" />}
        </button>
        {open && (
          <div className="ml-6 mt-0.5 space-y-0.5">
            {visibleChildren.map(child => (
              <NavLink key={child.path} to={child.path}
                style={({ isActive }) => ({ color: isActive ? undefined : '#333', backgroundColor: isActive ? 'var(--brand-primary, #1d4ed8)' : undefined })}
                className={({ isActive }) => `flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm transition-colors ${isActive ? 'text-white font-semibold' : 'hover:bg-gray-100'}`}>
                <span className="">›</span>
                {(child.roleLabels && child.roleLabels[role]) || child.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }
  return (
    <NavLink to={item.path}
      style={({ isActive }) => ({ color: isActive ? undefined : '#000', backgroundColor: isActive ? 'var(--brand-primary, #1d4ed8)' : undefined })}
      className={({ isActive }) => `flex items-center gap-2 px-2 py-1 rounded-lg text-sm transition-colors ${isActive ? 'text-white font-semibold' : 'hover:bg-gray-100'}`}>
      <item.icon size={16} className="flex-shrink-0" />
      <span className="font-normal">{item.label}</span>
    </NavLink>
  );
}

function Sidebar({ mobile, onClose }) {
  const { currentUser, logout } = useAuth();
  const { brandLogo, schoolName } = useApp();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className={`${mobile ? 'fixed inset-y-0 left-0 z-50 w-64' : 'relative w-full'} flex flex-col h-full bg-white border-r border-gray-200`}>
      {/* Logo / School name */}
      <div className="flex items-center justify-between border-b border-gray-200 flex-shrink-0 px-3 py-2">
        <div className="flex-1 flex items-center justify-center">
          {brandLogo
            ? <img src={brandLogo} alt={schoolName} style={{ maxHeight: '52px', width: 'auto', maxWidth: '210px' }} className="object-contain" />
            : <span className="text-sm font-semibold text-gray-700 text-center px-1">{schoolName || 'School ERP'}</span>
          }
        </div>
        {mobile && <button onClick={onClose} className="text-gray-400 hover:text-gray-700 pl-2"><X size={20} /></button>}
      </div>

      <nav className="flex-1 overflow-y-auto sidebar-scroll p-2 space-y-0">
        {navConfig.map(item => <NavItem key={item.label} item={item} role={currentUser?.role} />)}
      </nav>

      <div className="p-3 border-t border-gray-200">
        <div className="flex items-center gap-2.5 px-2 mb-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold"
            style={{ backgroundColor: '#16a34a' }}>
            {currentUser?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
          </div>
          <div className="min-w-0">
            <p className="text-gray-800 text-sm font-medium truncate">{currentUser?.name}</p>
            <p className="text-gray-400 capitalize">{currentUser?.role}</p>
          </div>
        </div>
        <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-1 rounded-lg text-sm hover:bg-gray-100 transition-colors">
          <LogOut size={16} /><span>Logout</span>
        </button>
      </div>
    </div>
  );
}
export default Sidebar;
